// Scene 3 - Precheck + Artefakt-Diff + globale Referenzen.
// Startet den Precheck, sobald der Bucket-Set (Werke + Package) sich aendert.
// Baut die Zeilen aus state.precheckByBucket auf.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLang } from "../i18n";
import { useTransportV2, packageStates, usesWerke, buckets, visBuckets, activeWerke, gewaehlteIflows, refNutzer, refGebraucht, autoDeploy, referenceAutoDeploy, referenceDeployRequired, bulkDeploymentChoices, manualEdgeRequested, bulkManualEdgeChoices, hasStageDeployment, packageDeployValue, packageDeployLocked, hasPackageStageDeployment, designEdgeRequested } from "../context/TransportV2Context";
import { Box, Button, Tooltip } from "@mui/material";
import DeploymentStages from "../components/DeploymentStages";
import DeploymentSwitch from "../components/DeploymentSwitch";
import { Cx } from "../components/IconSprite";
import TargetName from "../components/TargetName";
import { usePipelineReady } from "../hooks/usePipelineReady";
import { usePrecheckStream } from "../hooks/usePrecheckStream";
import { api } from "../../../utils/helpers";
import BpmnCompareDialog from "../../../components/BpmnCompareDialog";
import { needsSameVersionPermission, wasSameVersionSelected, isArtifactDraft, needsSameReferenceVersionPermission } from "../utils/sameVersion";
import { getDesignArtifacts } from "../utils/designArtifacts";
import { validPlantNaming } from "../utils/plantNaming";
import { isPackageTransport, packageDesignObjects } from "../utils/packageTransport";
import WholePackageSection, { FixedChoice, PackageCiWarning, PackageTransportNotes, packageTransportBlockOf } from "../components/WholePackageSection";
import { packageTransportText } from "../../../utils/transportPackageErrors";
import useExtensionVersion from "../hooks/useExtensionVersion";
import { PACKAGE_DEPLOY_MIN_EXTENSION } from "../context/TransportV2Context";

function mapStatus(s) {
  if (s === "new") return "new";
  if (s === "update") return "update";
  if (s === "identical") return "identical";
  if (s === "draft") return "draft";
  if (s === "orphan") return "orphan";
  // DEV181 C1: Ziel-ID liegt im Ziel in einem anderen Paket.
  if (s === "conflict") return "conflict";
  return s || "identical";
}

const REF_STATES = new Set(["present", "older", "newer", "missing", "unknown"]);
const REF_STATUS_COLOR = { present: "var(--iio-success)", older: "var(--iio-warning)", newer: "var(--iio-text-2)", missing: "var(--iio-critical)", unknown: "var(--iio-text-3)" };

const ST_CLS = { new: "info", update: "info", identical: "idle", draft: "warn", orphan: "warn", conflict: "crit" };

// Rang einer Referenz in der SAP-Deploy-Reihenfolge. Zuerst der Typ-Schluessel (typ), als
// Rueckfall das Label (Type) fuer Antworten eines aelteren Backends ohne typ.
const REF_TYP_RANG = { messagemapping: 0, scriptcollection: 1, valuemapping: 2 };
function refTypRang(r) {
  const key = String(r?.typ || r?.type || "").replace(/\s+/g, "").toLowerCase();
  const rang = REF_TYP_RANG[key];
  return rang === undefined ? 3 : rang;
}

function cmpVer(a, b) {
  const A = String(a || "").split(".").map((n) => parseInt(n, 10) || 0);
  const B = String(b || "").split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(A.length, B.length); i++) {
    const d = (A[i] || 0) - (B[i] || 0);
    if (d) return d;
  }
  return 0;
}

// Beide Tabellen teilen sich Spaltenbreiten und Zellabstände.
function ArtifactColumns() {
  return <colgroup><col className="transport-col-choice" /><col /><col className="transport-col-versions" />
    <col className="transport-col-status" /><col className="transport-col-deploy" /><col className="transport-col-deploy" /><col className="transport-col-action" /></colgroup>;
}

function Versions({ source, target }) {
  const { t } = useLang();
  return <span className="transport-versions"><span title={t("th.src")}>{source || "-"}</span>
    <Cx /><span title={t("th.tgt")}>{target || "-"}</span></span>;
}

export default function Scene3Artifacts() {
  const { t } = useLang();
  const { state, dispatch } = useTransportV2();
  const localStream = usePrecheckStream();
  const multiPackage = (state.selectedPackages || []).length > 1;
  const stream = multiPackage ? {
    phases: state.precheckPhases || [],
    running: ["idle", "queued", "running"].includes(state.precheckState),
    error: state.precheckError ? t("batch.compareError") : null,
    refsWarning: state.precheckRefsWarning ? t("batch.referencesError") : null,
  } : { ...localStream, refsWarning: localStream.refsWarning
    || (state.precheckState === "partial" && state.precheckRefsWarning ? t("batch.referencesError") : null) };
  const pipeline = usePipelineReady(state.spaceId);
  const deploymentChoices = bulkDeploymentChoices(state);
  const deploymentAll = deploymentChoices.length > 0 && deploymentChoices.every(Boolean);
  const manualEdgeChoices = bulkManualEdgeChoices(state);
  const manualEdgeAll = manualEdgeChoices.length > 0 && manualEdgeChoices.every(Boolean);
  const deploymentAvailable = !pipeline.loading && pipeline.ready && pipeline.modus !== "sap";
  const [modal, setModal] = useState(null);
  const [release, setRelease] = useState(null);
  const startedRef = useRef(null);
  // Erzwingt einen frischen Precheck-Lauf (der Effekt unten haengt daran): die
  // Bucket-Signatur allein aendert sich nicht, wenn nur der iFlow-INHALT in der
  // Quelle geaendert wurde.
  const [reloadNonce, setReloadNonce] = useState(0);
  const [reloadPending, setReloadPending] = useState(false);
  const reloadSelectionRef = useRef(Object.keys(state.precheckByBucket || {}).length ? { sel: state.sel, precheck: state.precheckByBucket } : null);

  const bucketList = buckets(state);
  const visible = visBuckets(state);
  const withWerke = usesWerke(state);

  // Precheck starten wenn Bucket-Signatur wechselt
  useEffect(() => {
    if (multiPackage) return;
    if (state.step !== 3) return;
    if (!state.sourceInstanceId || !state.pkgId || !validPlantNaming(state.plantNaming)) return;
    const sig = JSON.stringify([state.spaceKey, state.pkgId, state.packageEpoch, state.plantNaming, bucketList.map(b => b.key)]);
    if (startedRef.current === sig) return;
    startedRef.current = sig;
    if (reloadNonce === 0 && ["done", "partial"].includes(state.precheckState) && Object.keys(state.precheckByBucket || {}).length > 0) return;
    dispatch({ type: "SET_PRECHECK_STATE", payload: "running" });
    stream.reset();
    const werke = withWerke ? bucketList.map((b) => ({ key: b.key, kennung: b.kennung, name: b.name })) : [];
    const targetInst = state.space?.qasInstance;
    if (!targetInst) return;
    const controller = new AbortController();
    let alive = true;
    stream.run({
      sourceInstanceId: state.sourceInstanceId,
      sourcePackageId: state.pkgId,
      targetInstances: [targetInst],
      werke, plantNaming: state.plantNaming, signal: controller.signal,
    }).then(result => {
      if (!alive) return;
      const precheck = result?.precheck || {};
      dispatch({ type: "SET_PRECHECK", payload: precheck });
      dispatch({ type: "SET_GLOBAL_REFS", payload: result?.references || [] });
      const sel = {}, expanded = {};
      const whole = isPackageTransport({ precheckByBucket: precheck });
      for (const entry of Object.values(precheck)) {
        const werkKey = entry.werk?.kennung || "std";
        for (const a of entry.comparison || []) {
          const src = a.originalId || a.Id;
          if (!sel[src]) sel[src] = {};
          expanded[src] = true;
          // Pakettransport: jede freigegebene Version ist fest ausgewählt.
          if (whole) { if (!isArtifactDraft(a)) sel[src][werkKey] = true; continue; }
          const saved = reloadSelectionRef.current;
          const wasSelected = !saved || saved.releasedId === src || !Object.prototype.hasOwnProperty.call(saved.sel || {}, src)
            ? true : !!saved.sel[src]?.[werkKey];
          if ((!needsSameVersionPermission(a) || (state.canTransportSameVersion && wasSameVersionSelected(reloadSelectionRef.current?.precheck, reloadSelectionRef.current?.sel, a, werkKey))) && !isArtifactDraft(a) && wasSelected) sel[src][werkKey] = true;
        }
      }
      dispatch({ type: "SET_SEL", payload: sel });
      dispatch({ type: "SET_EXPANDED", payload: expanded });
      dispatch({ type: "SET_PRECHECK_STATE", payload: result?.referencesIncomplete ? "partial" : "done" });
    }).catch(() => { if (alive) dispatch({ type: "SET_PRECHECK_STATE", payload: "error" }); })
      .finally(() => { if (alive) setReloadPending(false); });
    return () => { alive = false; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.step, state.pkgId, state.spaceKey, state.werkOn, state.packageEpoch, state.plantNaming, multiPackage, reloadNonce]);

  // Die letzte Liste bleibt sichtbar, bis der neue Vergleich vorliegt.
  // Ein Ref allein startet keinen Effekt; reloadNonce ist der Auslöser.
  const refresh = useCallback((releasedId = null) => {
    if (multiPackage) {
      dispatch({ type: "PACKAGE_PRECHECK_REQUEST", pkgId: state.pkgId, generation: state.precheckGeneration, releasedId });
      return;
    }
    dispatch({ type: "SET_PACKAGE_PREPARED", payload: false });
    startedRef.current = null;
    reloadSelectionRef.current = { sel: state.sel, precheck: state.precheckByBucket, releasedId: releasedId || (stream.error ? reloadSelectionRef.current?.releasedId : null) };
    setReloadPending(true);
    setReloadNonce((n) => n + 1);
  }, [state.sel, state.precheckByBucket, state.pkgId, state.precheckGeneration, multiPackage, stream.error, dispatch]);

  // Aggregierte Zeilen aus precheck aufbauen
  const rows = useMemo(() => {
    const cmp = state.precheckByBucket || {};
    const byOrigId = new Map();
    for (const [, entry] of Object.entries(cmp)) {
      if (!entry || !Array.isArray(entry.comparison)) continue;
      const werkKey = entry.werk?.kennung || "std";
      const werk = entry.werk ? { key: werkKey, name: entry.werk.name, kennung: entry.werk.kennung } : { key: "std", name: "Standard", kennung: "" };
      for (const a of entry.comparison) {
        const origId = a.originalId || a.Id;
        if (!byOrigId.has(origId)) {
          byOrigId.set(origId, {
            id: origId,
            name: a.Name || origId,
            type: a.Type || "Integration Flow",
            srcVer: a.Version || "-",
            draft: isArtifactDraft(a),
            lastVer: a.lastVer || a.Version || "1.0.0",
            werkeStatus: {},
            werke: [],
          });
        }
        const target = byOrigId.get(origId);
        target.werkeStatus[werkKey] = { sameVersion: needsSameVersionPermission(a), status: mapStatus(a.status), tgt: a.targetVersion || null, suffixedId: a.suffixedId || a.Id,
          ...(a.conflict?.message ? { conflict: a.conflict.message } : {}),
          // Zielname wie im Dialog „Zielnamen beim Transport“: nur der Servername, nie aus dem Quellnamen gebaut.
          targetName: entry.werk?.kennung && typeof a.targetName === "string" && a.targetName ? a.targetName : null, instanceId: entry.instance?.id };
        target.werke.push({ ...werk, ...target.werkeStatus[werkKey] });
      }
    }
    // sort: draft ganz nach oben, dann alphabetisch
    return [...byOrigId.values()].sort((a, b) => {
      if (a.draft !== b.draft) return a.draft ? -1 : 1;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [state.precheckByBucket]);

  const selectedRowsCount = useMemo(() => {
    let n = 0;
    for (const [src, werkSel] of Object.entries(state.sel || {})) {
      for (const b of bucketList) {
        if (werkSel && werkSel[b.key]) n++;
        void src;
      }
    }
    return n;
  }, [state.sel, bucketList]);

  const counts = useMemo(() => {
    const c = { new: 0, update: 0, identical: 0, draft: 0 };
    for (const row of rows) {
      if (row.draft) { c.draft += 1; continue; }
      for (const b of visible) {
        const info = row.werkeStatus[b.key];
        if (info) c[info.status] = (c[info.status] || 0) + 1;
      }
    }
    return c;
  }, [rows, visible]);

  const openModal = (row, werkKey) => {
    const info = row.werkeStatus[werkKey];
    if (!info) return;
    setModal({
      artId: row.id,
      artName: row.name,
      sourceInstanceId: state.sourceInstanceId,
      targetInstanceId: info.instanceId,
      targetArtId: info.suffixedId,
      srcVer: row.srcVer,
      tgtVer: info.tgt,
    });
  };

  const openRelease = useCallback(async (row) => {
    setRelease({ artId: row.id, loading: "info", version: "", floor: null, error: null });
    try {
      const d = await api(`/api/${state.sourceInstanceId}/transport/artifact-last-version/${encodeURIComponent(row.id)}?packageId=${encodeURIComponent(state.pkgId)}`);
      setRelease((p) => (p && p.artId === row.id ? { ...p, loading: null, floor: d.floor || null, version: d.suggested || d.lastVersion || row.lastVer || "1.0.1" } : p));
    } catch (e) {
      setRelease((p) => (p && p.artId === row.id ? { ...p, loading: null, version: row.lastVer || "1.0.1", error: e.message } : p));
    }
  }, [state.sourceInstanceId, state.pkgId]);

  const doRelease = useCallback(async (row) => {
    const r = release;
    if (!r || r.artId !== row.id) return;
    const ver = (r.version || "").trim();
    if (!/^\d+(\.\d+){1,3}$/.test(ver)) { setRelease({ ...r, error: "Ungültiges Format" }); return; }
    if (r.floor && cmpVer(ver, r.floor) <= 0) { setRelease({ ...r, error: `Version muss größer als ${r.floor} sein` }); return; }
    setRelease({ ...r, loading: "release", error: null });
    try {
      await api(`/api/${state.sourceInstanceId}/transport/release-version`, {
        method: "POST",
        body: JSON.stringify({ flowId: row.id, version: ver, packageId: state.pkgId, floor: r.floor || null }),
      });
      setRelease(null);
      refresh(row.id);
    } catch (e) {
      setRelease((p) => (p && p.artId === row.id ? { ...p, loading: null, error: e.message } : p));
    }
  }, [release, state.sourceInstanceId, state.pkgId, refresh]);

  const nSelectable = (row) => visible.filter((b) => {
    const info = row.werkeStatus[b.key];
    return info && !info.sameVersion;
  }).length;
  const nOn = (row) => visible.filter((b) => !row.werkeStatus[b.key]?.sameVersion && state.sel?.[row.id]?.[b.key]).length;

  const onToggle = (rowId, werkKey, on) => dispatch({ type: "TOGGLE_SEL", payload: { sourceId: rowId, werkKey, on } });

  const onParent = (row, on) => {
    for (const b of visible) {
      const info = row.werkeStatus[b.key];
      if (info && !info.sameVersion) onToggle(row.id, b.key, on);
    }
  };

  // Ohne Punkt davor: das farbige Statuswort traegt die Bedeutung selbst,
  // der Dot davor war reine Doppelung (Davids Feedback 27.08.).
  const StatusPill = ({ status, message }) => {
    const cls = ST_CLS[status] || "idle";
    // t() fällt für unbekannte Statuswerte auf einen lesbaren Text zurück, nie auf "st.<wert>".
    const label = t("st." + status);
    if (status !== "conflict") return <span className={"st st--" + cls}>{label}</span>;
    // DEV181 C1: Konflikt mit Servertext an der Zeile; beim ganzen Paket sperrt er den Transport.
    return <>
      <span className={"st st--" + cls} aria-label={t("st.conflictAria")}>{label}</span>
      {message && <span className="transport-conflict-text" role="note" style={{ display: "block", color: "var(--crit)", fontSize: 12.5 }}>{message}</span>}
    </>;
  };

  // DEV183: Fremde globale Referenzen (checkOnly) werden nur geprüft; eigene Referenzen des Pakets bleiben wählbar.
  // Welle 6: Beim ganzen Paket ist keine Referenz wählbar. Eigene reisen im SAP-Export mit und stehen schon bei den
  // Designobjekten; der Abschnitt zeigt sie nicht noch einmal (siehe refsShown unten).
  const refs = useMemo(() => (state.globalRefs || []).filter((r) => r?.checkOnly !== true), [state.globalRefs]);
  const refChecks = useMemo(() => (state.globalRefs || []).filter((r) => r?.checkOnly === true).map((r) => {
    const raw = String(r?.targetStatus || r?.status || "").toLowerCase();
    const status = REF_STATES.has(raw) ? raw : "unknown";
    const packageName = r?.sourcePackageName || r?.sourcePackageId || "";
    return { id: r?.bundleId || r?.id || r?.Id || r?.name || r?.Name, name: r?.name || r?.Name || r?.Id || r?.id || "-",
      type: r?.type || r?.Type || "", packageId: r?.sourcePackageId || "", packageName,
      srcVer: r?.sourceVersion || r?.version || r?.Version || "-", tgtVer: r?.targetVersion || "-", status,
      hint: (typeof r?.hint === "string" && r.hint.trim()) || (["older", "missing"].includes(status) ? t("refCheck.transportFirst", { pkg: packageName || "-" }) : "") };
  }).filter((r) => r.id), [state.globalRefs, t]);
  // Welche iFlows sind im Transport? Daran haengt, ob eine Referenz gebraucht wird.
  const gewaehlt = useMemo(() => gewaehlteIflows(state.sel), [state.sel]);
  const refsMapped = useMemo(() => refs.map((r) => {
    // Id VOR Name: der Snapshot laedt die Referenz per Id - ein Name-Schluessel liefe bei
    // Name != Id in einen 404. Und package-references schreibt Type GROSS: nur r.type zu
    // lesen zeigte in der Typ-Spalte immer "Reference".
    const id = r?.bundleId || r?.id || r?.Id || r?.name || r?.Name;
    const name = r?.name || r?.Name || r?.bundleId || r?.id || r?.Id || "-";
    const type = r?.type || r?.Type || "Reference";
    const srcVer = r?.version || r?.srcVer || r?.srcVersion || r?.Version || "-";
    const tgtVer = r?.targetVersion || r?.tgtVer || "-";
    let statusRaw = r?.status;
    if (statusRaw) statusRaw = String(statusRaw).toLowerCase();
    else if (r?.targetVersion) statusRaw = r?.version === r?.targetVersion ? "identical" : "update";
    else statusRaw = "new";
    // resolvable=false: Referenz existiert nicht auf dem Quell-Tenant. Der
    // Reducer setzt refSel bereits hart auf false; hier deaktivieren wir die
    // Checkbox zusaetzlich in der UI und zeigen einen klaren Hinweis.
    const resolvable = r?.resolvable !== false;
    // Entwuerfe haben keine aktive Version - der Snapshot kann sie nicht laden und
    // wuerde den ganzen Transport abbrechen. Gleiche Sperre wie bei den iFlows.
    const draft = r?.draft === true || statusRaw === "draft" || String(srcVer).toLowerCase() === "draft";
    // Herkunft: die iFlows, die diese Referenz nutzen (referencedBy). Ist keiner davon im
    // Transport, wird die Referenz nicht gebraucht - und laesst sich auch nicht anwaehlen.
    const nutzer = refNutzer(r);
    const gebraucht = refGebraucht(r, gewaehlt);
    // v10: Typ-Schluessel (MessageMapping | ScriptCollection | ValueMapping | roher bundleType)
    // fuer die Sortierung; das Label in der Typ-Spalte kommt fertig vom Backend.
    const typ = r?.typ || r?.Typ || "";
    // Per Schnittstelle nicht transportierbar (Function Library u. a.): wird gemeldet, die
    // Checkbox bleibt gesperrt, der Grund kommt vom Backend (deutsch) - mit Rueckfalltext.
    const transportierbar = r?.transportierbar !== false;
    const grund = typeof r?.grund === "string" ? r.grund : "";
    // Herkunft "paket": Value Mapping aus dem Quellpaket ohne iFlow-Verweis (referencedBy leer).
    const ausPaket = r?.herkunft === "paket";
    const packageId = r?.packageId || r?.sourcePackageId || "";
    // Value Mapping, das im Ziel schon existiert: der Transport ersetzt dort die gepflegten
    // Werte. Die Checkbox bleibt bedienbar - die Freigabe ist eine bewusste Entscheidung.
    const ueberschreibt = r?.ueberschreibtZielwerte === true;
    return { id, name, type, typ, srcVer, tgtVer, status: statusRaw, sameVersion: needsSameReferenceVersionPermission(r), resolvable, draft, nutzer, gebraucht, transportierbar, grund, ausPaket, ueberschreibt, packageId };
  }).sort((a, b) => {
    // SAP-Deploy-Reihenfolge (Message Mapping -> Script Collection -> Value Mapping, dann
    // Unbekannte), innerhalb alphabetisch - der Nutzer sieht die Reihenfolge des Ziel-Workflows.
    const d = refTypRang(a) - refTypRang(b);
    return d || String(a.name || "").localeCompare(String(b.name || ""));
  }), [refs, gewaehlt]);

  const selectedDeployRefs = refsMapped.filter(r => state.refSel?.[r.id] && r.gebraucht && r.transportierbar && r.resolvable && !r.draft);
  const referenceDeploymentAll = selectedDeployRefs.length > 0 && selectedDeployRefs.every(r => referenceAutoDeploy(state, { id: r.id, referencedBy: r.nutzer }));
  const comparing = stream.running || reloadPending;
  const controlsBusy = comparing || release?.loading === "release";
  const referencesBlocked = pipeline.modus !== "sap" && !!stream.refsWarning;
  // Paket ohne iFlow: Der Vergleich ist fertig, enthält aber keine Artefakte. Nur Anzeige, Auswahl und Payload bleiben unberührt.
  const ohneIflows = (d) => ["done", "partial"].includes(d.precheckState) && Object.keys(d.precheckByBucket || {}).length > 0
    && Object.values(d.precheckByBucket).every((e) => e && Array.isArray(e.comparison) && e.comparison.length === 0);
  // Pakettransport (W6.5): Der Server transportiert das ganze Paket, auch ohne iFlow. Die Sofortstufe „nicht
  // transportierbar“ gilt nur noch, wenn der Server keine Transportart liefert (Notschalter) und das Paket keine iFlows hat.
  const wholePackage = isPackageTransport(state);
  const designObjects = useMemo(() => packageDesignObjects(state), [state.precheckByBucket]); // eslint-disable-line react-hooks/exhaustive-deps
  const nichtTransportierbar = (d) => ohneIflows(d) && !isPackageTransport(d);
  // v27: Extension Version laden um den Paketschalter zu sperren (Repo < 27 -> CI fest an).
  const extensionVersion = useExtensionVersion(wholePackage ? state.spaceId : null);
  useEffect(() => {
    if (wholePackage && extensionVersion !== null) dispatch({ type: "SET_EXTENSION_VERSION", payload: extensionVersion });
  }, [wholePackage, extensionVersion, dispatch]);
  const pkgDeployLocked = packageDeployLocked(state);
  const pkgDeploy = packageDeployValue(state);
  // Pakettransport: CI gesperrt auf den Paketzustand, Edge je Zeile frei wählbar.
  const cloudLocked = (name) => <DeploymentSwitch locked checked={pkgDeploy} name={name} hint={t("pkgT.ciLockedHint")} onChange={() => {}} />;
  const edgeSwitch = (name, sourceId, werkKey) => <DeploymentSwitch edge name={name}
    checked={manualEdgeRequested(state, sourceId, werkKey)} customized={hasStageDeployment(state, sourceId, werkKey, true)}
    disabled={!deploymentAvailable || controlsBusy || !!state.batch || !!state.batchRequest}
    onChange={on => dispatch({ type: "SET_MANUAL_EDGE_DEPLOY", payload: { sourceId, werkKey, on } })} />;
  const designEdgeSwitch = (name, designId) => <DeploymentSwitch edge name={name}
    checked={designEdgeRequested(state, designId)}
    disabled={!deploymentAvailable || controlsBusy || !!state.batch || !!state.batchRequest}
    onChange={on => dispatch({ type: "SET_DESIGN_EDGE_DEPLOY", payload: { designId, on } })} />;
  // Eigene Referenz des Pakets: Herkunft "paket" oder Quellpaket gleich dem transportierten Paket. Beim ganzen Paket entfällt
  // sie hier, weil sie bei den Designobjekten steht. Übrig bleiben nur fremde Referenzen, die ohne checkOnly ankommen
  // (Notschalter); sie sind schreibgeschützt. Ohne solche entfällt der Abschnitt.
  const refInPackage = (r) => r.ausPaket || (!!r.packageId && r.packageId === state.pkgId);
  const refsShown = wholePackage ? refsMapped.filter((r) => !refInPackage(r)) : refsMapped;
  // Beim ganzen Paket steht ein Designobjekt des Pakets schon in der Tabelle; als geprüfte Referenz erschiene es doppelt.
  const refChecksShown = wholePackage ? refChecks.filter((r) => !designObjects.some((o) => o.id === r.id)) : refChecks;
  const leerPaket = !comparing && !stream.error && nichtTransportierbar(state);
  // Pakete ohne iFlow sperren Weiter: Sie landen sonst in der Nutzlast und der Snapshot lehnt die leere Artefaktliste ab.
  const leerePakete = comparing || stream.error ? [] : (multiPackage ? packageStates(state) : [state]).filter(nichtTransportierbar).map((d) => d.pkgName || d.pkgId);
  const designArtifacts = leerPaket && !multiPackage ? getDesignArtifacts(state.sourceInstanceId, state.pkgId) : [];
  // M7: packageTransportBlocked im Precheck sperrt Weiter (der Server lehnt den Transport ohnehin ab), mit Grund im Fuß.
  const packageBlocked = !comparing && !stream.error && !!packageTransportBlockOf(Object.values(state.precheckByBucket || {}));
  const canNext = !packageBlocked && (selectedRowsCount > 0 || wholePackage) && leerePakete.length === 0 && !controlsBusy && !stream.error && !referencesBlocked;

  // "Alle auswaehlen" ueber die Kopf-Checkbox: nur wirklich waehlbare Zeilen
  // (nicht identisch, kein Entwurf). indeterminate zeigt eine Teilauswahl an.
  const totalSelectable = useMemo(() => {
    let n = 0;
    for (const row of rows) {
      if (row.draft) continue;
      n += nSelectable(row);
    }
    return n;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, visible]);
  const selectedBulkCount = rows.filter(row => !row.draft).reduce((n, row) => n + nOn(row), 0);
  const setAllRows = (on) => {
    for (const row of rows) {
      if (row.draft) continue;
      for (const b of visible) {
        const info = row.werkeStatus[b.key];
        if (info && !info.sameVersion) onToggle(row.id, b.key, on);
      }
    }
  };
  // Die Kopf-Checkbox nimmt nur, was sich gefahrlos sammeln laesst: transportierbar, aufloesbar,
  // kein Entwurf, nicht identisch, gebraucht - und KEIN Value Mapping, das Zielwerte
  // ueberschreiben wuerde. Das hakt der Nutzer nur explizit je Zeile an.
  const selectableRefs = useMemo(
    () => refsMapped.filter((r) => r.transportierbar && r.resolvable && !r.draft && !r.sameVersion && r.gebraucht && !r.ueberschreibt),
    [refsMapped]
  );
  const selectedRefCount = selectableRefs.filter((r) => !!state.refSel[r.id]).length;
  const setAllRefs = (on) => {
    for (const r of selectableRefs) {
      if (!!state.refSel[r.id] !== on) dispatch({ type: "TOGGLE_REF", payload: r.id });
    }
  };
  const headCbRef = (all, some) => (el) => { if (el) el.indeterminate = some && !all; };

  return (
    <section className="scene active scene-enter" id="s3">
      <div className="sh">
        <h2>{t("s3.h2")}</h2>
        <p>{t(withWerke ? "s3.p" : "s3.singleInfo")}</p>
      </div>

      {(comparing || stream.phases.some((p) => p.status === "active")) && (
        <div className="panel panel-pad">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 14, flexWrap: "wrap", marginBottom: 6 }}>
            <h3 style={{ margin: 0, fontSize: 15 }}>{t(Object.keys(state.precheckByBucket || {}).length ? "s3.refreshing" : "s3.loading")}</h3>
            <span className="tiny muted">
              {withWerke ? t("s3.par", { n: activeWerke(state).length }) : t("s3.single")}
            </span>
          </div>
          <div>
            {[
              { id: "auth", label: t("s3.ph1") },
              { id: "source", label: t("s3.ph2") },
              { id: "target", label: t("s3.ph3") },
              { id: "refs", label: t("s3.ph4") },
              { id: "diff", label: t("s3.ph5") },
            ].map((ph) => {
              const it = stream.phases.find((x) => x.id === ph.id);
              const st = it?.status || "waiting";
              const cls = st === "done" ? "done" : st === "active" ? "run" : "";
              return (
                <div className={"phase-li " + cls} key={ph.id}>
                  <span className="ph-ind">
                    {st === "active" && <span className="spinner" />}
                    {st === "done" && <svg className="ic ic-16 ph-check"><use href="#ic-check" /></svg>}
                  </span>
                  <span>{ph.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {referencesBlocked && <div className="errline" role="alert"><span>{t("batch.referencesError")}</span>
        <button className="btn btn-sec btn-sm" disabled={controlsBusy} onClick={() => refresh()}>{t("err.retry")}</button></div>}
      {stream.error && (
        <div className="errline">
          <span style={{ flex: 1 }}>{t("err.precheck")}: {stream.error}</span>
          <button className="btn btn-sec btn-sm" onClick={() => refresh()} disabled={controlsBusy}>{t("err.retry")}</button>
        </div>
      )}

      {leerPaket && (
        <div className="panel" role="status">
          <div className="panel-pad">
            <div className="tiny" style={{ color: "var(--warn)" }}>{t("s3.noIflows", { pkg: state.pkgName || state.pkgId || "-" })}</div>
            {designArtifacts.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div className="tiny muted">{t("s3.noIflowsMappings")}</div>
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  {designArtifacts.map((a) => <li key={a.Id}><span className="art-name">{a.Name}</span> <span className="art-id">{a.Id} · {a.Type}</span></li>)}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {!comparing && !stream.error && <PackageTransportNotes state={state} />}

      {!leerPaket && Object.keys(state.precheckByBucket || {}).length > 0 && (
        <fieldset disabled={controlsBusy || !!stream.error} aria-busy={comparing} style={{ border: 0, padding: 0, margin: 0, marginTop: comparing ? 16 : 0, minWidth: 0 }}>
          {wholePackage && <WholePackageSection designObjects={designObjects} iflowCount={rows.length} state={state} />}
          {(!wholePackage || rows.length > 0 || designObjects.length > 0) && <div className="panel">
            <div className="panel-h artifact-panel-head">
              <div className="artifact-panel-title">
                <h3>{t("s3.tableTitle")}</h3>
                {/* Beim ganzen Paket gibt es weder Zahnrad noch Schalter zum Umstellen; beide Hinweise (auch der SAP Modus) entfallen. */}
                {!wholePackage && <div className="deployment-guidance">
                  {t(pipeline.modus === "sap" ? "deploy.sap" : "deploy.hint", { stage: String(state.space?.zielStufe || state.space?.qasInstance?.stage || "").toUpperCase() })}
                </div>}
              </div>
              <div className="artifact-panel-actions">
                <button className="btn btn-sec btn-sm" onClick={() => refresh()} title={t("s3.refreshTitle")}>
                  <svg className="ic ic-16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 4v6h-6M1 20v-6h6" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                  </svg>
                  {t("s3.refresh")}
                </button>
                <div className="diffcounts">
                  {counts.update > 0 && <span style={{ color: "var(--info)" }}><b>{counts.update}</b> {t("st.update")}</span>}
                  {counts.new > 0 && <span style={{ color: "var(--info)" }}><b>{counts.new}</b> {t("st.new")}</span>}
                  {counts.identical > 0 && <span className="muted"><b>{counts.identical}</b> {t("st.identical")}</span>}
                  {counts.draft > 0 && <span style={{ color: "var(--warn)" }}><b>{counts.draft}</b> {t("st.draft")}</span>}
                </div>
              </div>
            </div>
            {withWerke && (
              <div style={{ display: "block", padding: "14px 26px 0" }}>
                <div className="utabs" style={{ marginBottom: 0, borderBottom: "none" }}>
                  <button className={"utab" + (state.workingTab === "all" ? " on" : "")} onClick={() => dispatch({ type: "SET_WORKING_TAB", payload: "all" })}>
                    {t("s3.all", { n: bucketList.length })}
                  </button>
                  {bucketList.map((b) => (
                    <button key={b.key} className={"utab" + (state.workingTab === b.key ? " on" : "")} onClick={() => dispatch({ type: "SET_WORKING_TAB", payload: b.key })}>
                      {b.name} <span className="ken">{b.kennung}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="panel-pad tablewrap" style={{ paddingTop: 12 }}>
              <table className="dt transport-table">
                <ArtifactColumns />
                <thead>
                  <tr>
                    <th className="transport-choice">
                      {wholePackage ? <FixedChoice ariaLabel={t("pkgT.allFixed")} /> : <input
                        type="checkbox"
                        className="cb"
                        aria-label={t("s3.selAll")}
                        title={t("s3.selAll")}
                        checked={totalSelectable > 0 && selectedBulkCount >= totalSelectable}
                        disabled={totalSelectable === 0}
                        ref={headCbRef(selectedBulkCount >= totalSelectable, selectedBulkCount > 0)}
                        onChange={(e) => setAllRows(e.target.checked)}
                      />}
                    </th>
                    <th>{t("th.art")}</th>
                    <th>{t("deploy.versions", { cx: <Cx /> })}</th>
                    <th>{t("th.status")}</th>
                    {/* Pakettransport: Paketschalter für CI + Zahnrad für Stufenabweichungen; Edge nur als Beschriftung. */}
                    {wholePackage ? <><th><Box sx={{ display: "inline-flex", alignItems: "center", flexWrap: "nowrap", whiteSpace: "nowrap", gap: 0.5 }}>
                      <Tooltip title={t(pkgDeployLocked ? "pkgT.deployLockedHint" : "pkgT.deployHint")}><span><Button className="deployment-heading"
                        aria-label={t("deploy.bulkLabel")} aria-pressed={pkgDeploy}
                        disabled={pkgDeployLocked || !deploymentAvailable || controlsBusy || !!state.batch || !!state.batchRequest}
                        onClick={() => dispatch({ type: "SET_PACKAGE_DEPLOY", payload: !pkgDeploy })}>{t("deploy.heading")}</Button></span></Tooltip>
                      <DeploymentStages key={`${state.pkgId}-pkg-ci`} forPackage disabled={pkgDeployLocked || !deploymentAvailable || controlsBusy || !!state.batch || !!state.batchRequest}/>
                    </Box></th><th>{t("deploy.edge")}</th></> : <><th><Box sx={{ display: "inline-flex", alignItems: "center", flexWrap: "nowrap", whiteSpace: "nowrap", gap: 0.5 }}><Tooltip title={t("deploy.bulkHint")}><span><Button className="deployment-heading"
                      aria-label={t("deploy.bulkLabel")} aria-pressed={deploymentAll}
                      disabled={!deploymentAvailable || !deploymentChoices.length || !!state.batch || !!state.batchRequest}
                      onClick={() => dispatch({ type: "SET_ALL_DEPLOY", payload: !deploymentAll })}>{t("deploy.heading")}</Button></span></Tooltip><DeploymentStages key={`${state.pkgId}-ci`} disabled={!deploymentAvailable || !deploymentChoices.length || controlsBusy || !!state.batch || !!state.batchRequest}/></Box></th>
                    <th><Box sx={{ display: "inline-flex", alignItems: "center", flexWrap: "nowrap", whiteSpace: "nowrap", gap: 0.5 }}><Tooltip title={t("deploy.edgeManualHint")}><span><Button className="deployment-heading"
                      aria-label={t("deploy.edgeBulkLabel")} aria-pressed={manualEdgeAll}
                      disabled={!deploymentAvailable || !manualEdgeChoices.length || controlsBusy || !!state.batch || !!state.batchRequest}
                      onClick={() => dispatch({ type: "SET_ALL_MANUAL_EDGE_DEPLOY", payload: !manualEdgeAll })}>{t("deploy.edge")}</Button></span></Tooltip><DeploymentStages key={`${state.pkgId}-edge`} edge disabled={!deploymentAvailable || !manualEdgeChoices.length || controlsBusy || !!state.batch || !!state.batchRequest}/>
                    </Box></th></>}
                    <th>{t("th.action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    if (row.draft) {
                      const isEditing = release && release.artId === row.id;
                      return (
                        <tr key={row.id}>
                          {wholePackage ? <td className="transport-choice"><FixedChoice label={row.name} /></td> : <td />}
                          <td>
                            <span className="art-name">{row.name}</span><br />
                            <span className="art-id">{t("s3.draftLast", { id: row.id, v: row.lastVer })} · {row.type}</span>
                          </td>
                          <td><Versions source={t("st.draft")} /></td>
                          <td><StatusPill status="draft" /></td>
                          <td><span className="transport-no-deploy">{t("deploy.releaseFirst")}</span></td>
                          <td />
                          <td>
                            {!isEditing && (
                              <button className="btn btn-sec btn-sm" onClick={() => openRelease(row)}>{t("s3.release")}</button>
                            )}
                            {isEditing && (
                              <span className="draft-editor">
                                <input
                                  className={"inp" + (release?.error ? " inp--invalid" : "")}
                                  value={release.version}
                                  onChange={(e) => setRelease({ ...release, version: e.target.value, error: null })}
                                  aria-label="Version"
                                />
                                <button className="btn btn-sm btn-sec" onClick={() => doRelease(row)} disabled={release.loading}>
                                  {t("s3.doRel")}
                                </button>
                                <span className="tiny muted">{t("s3.lastVer", { v: release.floor || row.lastVer })}</span>
                                {release.error && <span className="tiny" style={{ color: "var(--crit)" }}>{release.error}</span>}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    }

                    if (!withWerke) {
                      const info = row.werkeStatus.std;
                      if (!info) return null;
                      const ident = info.status === "identical";
                      const sel = !!state.sel?.[row.id]?.std;
                      return (
                        <tr key={row.id}>
                          <td className="transport-choice">
                            {wholePackage ? <FixedChoice label={row.name} /> : <input
                              type="checkbox"
                              className="cb"
                              checked={sel}
                              disabled={info.sameVersion && !state.canTransportSameVersion}
                              title={info.sameVersion ? t(state.canTransportSameVersion ? "s3.sameVersionChoice" : "s3.sameVersionBlocked") : undefined}
                              aria-label={t("deploy.transportLabel", { name: row.name })}
                              onChange={(e) => onToggle(row.id, "std", e.target.checked)}
                            />}
                          </td>
                          <td>
                            <span className="art-name">{row.name}</span><br />
                            <span className="art-id">{row.id} · {row.type}</span>
                          </td>
                          <td><Versions source={row.srcVer} target={info.tgt} /></td>
                          <td><StatusPill status={info.status} message={info.conflict} /></td>
                          <td>{sel && wholePackage ? cloudLocked(row.name) : sel ? <DeploymentSwitch customized={hasStageDeployment(state, row.id, "std")} name={row.name} checked={autoDeploy(state, row.id, "std")} disabled={!deploymentAvailable}
                            onChange={on => dispatch({ type: "SET_DEPLOY", payload: { sourceId: row.id, werkKey: "std", on } })} />
                            : <span className="transport-no-deploy">{t(ident ? "s3.ident" : "deploy.notSelected")}</span>}</td>
                          <td>{sel && wholePackage ? edgeSwitch(row.name, row.id, "std") : sel && <DeploymentSwitch customized={hasStageDeployment(state, row.id, "std", true)} edge name={row.name} checked={manualEdgeRequested(state, row.id, "std")}
                            disabled={!deploymentAvailable || controlsBusy || !!state.batch || !!state.batchRequest}
                            onChange={on => dispatch({ type: "SET_MANUAL_EDGE_DEPLOY", payload: { sourceId: row.id, werkKey: "std", on } })} />}</td>
                          <td>
                            {info.status === "update" && (
                              <button className="btn btn-sec btn-sm" onClick={() => openModal(row, "std")}>{t("s3.view")}</button>
                            )}
                          </td>
                        </tr>
                      );
                    }

                    const selectable = nSelectable(row);
                    const chosen = nOn(row);
                    const agg = {};
                    for (const b of visible) {
                      const info = row.werkeStatus[b.key];
                      if (!info) continue;
                      agg[info.status] = (agg[info.status] || 0) + 1;
                    }
                    const aggTxt = Object.entries(agg).map(([s, n]) => `${n} ${t("st." + s)}`).join(" · ");
                    const open = !!state.expanded[row.id];
                    return (
                      <React.Fragment key={row.id}>
                        <tr>
                          <td>
                            {wholePackage ? <FixedChoice label={row.name} /> : <input
                              type="checkbox"
                              className="cb"
                              checked={chosen === selectable && selectable > 0}
                              disabled={selectable === 0}
                              onChange={(e) => onParent(row, e.target.checked)}
                            />}
                          </td>
                          <td>
                            <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                              <button
                                className={"exp-btn" + (open ? " open" : "")}
                                onClick={() => dispatch({ type: "TOGGLE_EXPAND", payload: row.id })}
                                aria-label="Werke aufklappen"
                              >
                                <svg className="ic ic-16"><use href="#ic-chevron-right" /></svg>
                              </button>
                              <span>
                                <span className="art-name">{row.name}</span><br />
                                <span className="art-id">{row.id} · {row.type} · {t("s3.plain", { n: visible.length })}</span>
                              </span>
                            </span>
                          </td>
                          <td><Versions source={row.srcVer} /></td>
                          <td><span className="tiny muted">{aggTxt}</span></td>
                          <td />
                          <td />
                          <td />
                        </tr>
                        {open && visible.map((b) => {
                          const info = row.werkeStatus[b.key];
                          if (!info) return null;
                          const ident = info.status === "identical";
                          const sel = !!state.sel?.[row.id]?.[b.key];
                          const label = (info.targetName || row.name) + " · " + b.name;
                          return (
                            <tr className="childrow" key={row.id + "-" + b.key}>
                              <td className="transport-choice">
                                {wholePackage ? <FixedChoice label={label} /> : <input
                                  type="checkbox"
                                  className="cb"
                                  checked={sel}
                                  disabled={info.sameVersion && !state.canTransportSameVersion}
                              title={info.sameVersion ? t(state.canTransportSameVersion ? "s3.sameVersionChoice" : "s3.sameVersionBlocked") : undefined}
                                  aria-label={t("deploy.transportLabel", { name: label })}
                                  onChange={(e) => onToggle(row.id, b.key, e.target.checked)}
                                />}
                              </td>
                              <td>
                                <span className="tree-cell">
                                  <span>
                                    <span className="art-name">{info.targetName
                                      ? <TargetName source={row.name} target={info.targetName} kennung={b.kennung} /> : b.name}</span>
                                    <span className="art-id" style={{ display: "block" }}>
                                      {info.suffixedId} · {row.type}
                                    </span>
                                  </span>
                                </span>
                              </td>
                              <td><Versions source={row.srcVer} target={info.tgt} /></td>
                              <td><StatusPill status={info.status} message={info.conflict} /></td>
                              <td>{sel && wholePackage ? cloudLocked(label) : sel ? <DeploymentSwitch customized={hasStageDeployment(state, row.id, b.key)} name={label} checked={autoDeploy(state, row.id, b.key)} disabled={!deploymentAvailable}
                                onChange={on => dispatch({ type: "SET_DEPLOY", payload: { sourceId: row.id, werkKey: b.key, on } })} />
                                : <span className="transport-no-deploy">{t(ident ? "s3.ident" : "deploy.notSelected")}</span>}</td>
                              <td>{sel && wholePackage ? edgeSwitch(label, row.id, b.key) : sel && <DeploymentSwitch customized={hasStageDeployment(state, row.id, b.key, true)} edge name={label} checked={manualEdgeRequested(state, row.id, b.key)}
                                disabled={!deploymentAvailable || controlsBusy || !!state.batch || !!state.batchRequest}
                                onChange={on => dispatch({ type: "SET_MANUAL_EDGE_DEPLOY", payload: { sourceId: row.id, werkKey: b.key, on } })} />}</td>
                              <td>
                                {info.status === "update" && (
                                  <button className="btn btn-sec btn-sm" onClick={() => openModal(row, b.key)}>{t("s3.view")}</button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                  {wholePackage && designObjects.map((object) => {
                    // Designobjekte beim ganzen Paket: dieselbe Zeilenform wie die iFlows, Haken fest, Deployment je Typ.
                    // v27: CI gesperrt auf Paketzustand, Edge je deploybarem Objekt frei wählbar.
                    const deployCell = object.deployable ? cloudLocked(object.name)
                      : <span className="transport-no-deploy">{t("pkgT.notDeployable")}</span>;
                    const edgeCell = object.deployable ? designEdgeSwitch(object.name, object.id) : null;
                    const describe = <span className="art-id" style={{ display: "block" }}>
                      {object.id} · {object.type}{object.withoutManifest ? ` · ${packageTransportText("withoutManifest")}` : ""}</span>;
                    if (!withWerke) {
                      const info = object.byWerk.std || {};
                      return <tr key={"design:" + object.id}>
                        <td className="transport-choice"><FixedChoice label={object.name} /></td>
                        <td><span className="art-name">{object.name}</span>{describe}</td>
                        <td><Versions source={object.version} target={info.targetVersion} /></td>
                        <td>{info.status && <StatusPill status={mapStatus(String(info.status).toLowerCase())} message={info.conflict} />}</td>
                        <td>{deployCell}</td>
                        <td>{edgeCell}</td><td />
                      </tr>;
                    }
                    const collapseKey = "design-collapsed:" + object.id;
                    const open = !state.expanded?.[collapseKey];
                    return <React.Fragment key={"design:" + object.id}>
                      <tr>
                        <td><FixedChoice label={object.name} /></td>
                        <td>
                          <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                            <button className={"exp-btn" + (open ? " open" : "")} onClick={() => dispatch({ type: "TOGGLE_EXPAND", payload: collapseKey })}
                              aria-label="Werke aufklappen" aria-expanded={open}>
                              <svg className="ic ic-16"><use href="#ic-chevron-right" /></svg>
                            </button>
                            <span><span className="art-name">{object.name}</span>{describe}</span>
                          </span>
                        </td>
                        <td><Versions source={object.version} /></td>
                        <td /><td>{deployCell}</td><td>{edgeCell}</td><td />
                      </tr>
                      {open && visible.map((b) => {
                        const info = object.byWerk[b.kennung] || object.byWerk[b.key] || {};
                        return <tr className="childrow" key={"design:" + object.id + "-" + b.key}>
                          <td className="transport-choice"><FixedChoice label={`${info.targetName || object.name} · ${b.name}`} /></td>
                          <td><span className="tree-cell"><span>
                            <span className="art-name">{info.targetName
                              ? <TargetName source={object.name} target={info.targetName} kennung={b.kennung} /> : b.name}</span>
                            <span className="art-id" style={{ display: "block" }}>
                              {info.targetId || packageTransportText("PACKAGE_TRANSPORT_TARGET_ID_LATER")} · {object.type}</span>
                          </span></span></td>
                          <td><Versions source={object.version} target={info.targetVersion} /></td>
                          <td>{info.status && <StatusPill status={mapStatus(String(info.status).toLowerCase())} message={info.conflict} />}</td>
                          <td /><td /><td />
                        </tr>;
                      })}
                    </React.Fragment>;
                  })}
                </tbody>
              </table>
            </div>
          </div>}

          {pipeline.modus === "sap" && <p className="tiny muted">{t("batch.sapReferences")}</p>}
          {pipeline.modus !== "sap" && refsShown.length > 0 && (
            <div className="sect panel">
              <div className="panel-h">
                <h3>{t("s3.refs")}</h3>
                {!wholePackage && <span className="meta">{t("s3.refsMeta")}</span>}
              </div>
              {!wholePackage && <div className="panel-pad" style={{ paddingTop: 12, paddingBottom: 0 }}>
                <p className="tiny muted" style={{ margin: 0 }}>{t("deploy.refsHint")}</p>
              </div>}
              <div className="panel-pad tablewrap" style={{ paddingTop: 12 }}>
                <table className="dt transport-table">
                  <ArtifactColumns />
                  <thead>
                    <tr>
                      <th className="transport-choice">
                        {!wholePackage && <input
                          type="checkbox"
                          className="cb"
                          aria-label={t("s3.selAllRefs")}
                          title={t("s3.selAllRefs")}
                          checked={selectableRefs.length > 0 && selectedRefCount >= selectableRefs.length}
                          disabled={selectableRefs.length === 0}
                          ref={headCbRef(selectedRefCount >= selectableRefs.length, selectedRefCount > 0)}
                          onChange={(e) => setAllRefs(e.target.checked)}
                        />}
                      </th>
                      <th>{t("th.art")}</th>
                      <th>{t("deploy.versions", { cx: <Cx /> })}</th>
                      <th>{t("th.status")}</th>
                      <th>{wholePackage ? t("deploy.heading") : <Button className="deployment-heading" aria-label={t("deploy.refsBulk")}
                        aria-pressed={referenceDeploymentAll} disabled={!deploymentAvailable || !selectedDeployRefs.some(r => !referenceDeployRequired(state, { referencedBy: r.nutzer })) || !!state.batch || !!state.batchRequest}
                        onClick={() => selectedDeployRefs.forEach(r => dispatch({ type: "SET_REF_DEPLOY", payload: { id: r.id, on: !referenceDeploymentAll } }))}>{t("deploy.heading")}</Button>}</th>
                      <th>{t("deploy.edge")}</th>
                      <th>{t("th.action")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {refsShown.map((r) => wholePackage ? (
                      // Pakettransport: nur fremde Referenzen, schreibgeschützt. SAPs Paketimport nimmt sie nicht mit, und sie gehen
                      // auch nicht als globale Referenz in den Snapshot (collectGlobalRefs).
                      <tr key={r.id}>
                        <td className="transport-choice" />
                        <td>
                          <span className="art-name">{r.name}</span>
                          <div className="art-id">{r.type}</div>
                        </td>
                        <td><Versions source={r.srcVer} target={r.tgtVer} /></td>
                        <td><StatusPill status={r.status} /></td>
                        <td /><td />
                        <td><span className="tiny muted">{t("pkgT.refOutside")}</span></td>
                      </tr>
                    ) : (
                      <tr key={r.id}>
                        <td className="transport-choice">
                          <input
                            type="checkbox"
                            className="cb"
                            checked={r.transportierbar && r.resolvable && !r.draft && r.gebraucht && !!state.refSel[r.id]}
                            disabled={(r.sameVersion && !state.canTransportSameVersionReferences) || !r.transportierbar || !r.resolvable || r.draft || !r.gebraucht}
                            title={r.sameVersion ? t(state.canTransportSameVersionReferences ? "s3.sameVersionChoice" : "s3.sameReferenceVersionBlocked") : undefined}
                            onChange={() => dispatch({ type: "TOGGLE_REF", payload: r.id })}
                            aria-label={t("deploy.transportLabel", { name: r.name })}
                          />
                        </td>
                        <td>
                          <span className="art-name">{r.name}</span>
                          <div className="art-id">{r.type}</div>
                          {r.ausPaket && r.nutzer.length === 0 && (
                            // Value Mapping aus dem Quellpaket ohne iFlow-Verweis: die Herkunft
                            // ist das Paket, nicht ein iFlow - gleiche dezente Zeile wie "aus <iFlow>".
                            <div className="tiny muted ref-from" style={{ marginTop: 2 }}>
                              {t("s3.refFromPkg", { pkg: state.pkgName || state.pkgId || "-" })}
                            </div>
                          )}
                          {r.nutzer.length > 0 && (
                            // Kurzform "aus A, B +n"; bei mehr als zwei Nutzern zeigt Hover oder Fokus
                            // die volle Liste (hoechstens zehn) an Ort und Stelle - ein Overlay wuerde
                            // der Scroll-Container der Tabelle abschneiden.
                            <div
                              className={"tiny muted ref-from" + (r.nutzer.length > 2 ? " ref-from--more" : "")}
                              style={{ marginTop: 2 }}
                              tabIndex={r.nutzer.length > 2 ? 0 : undefined}
                            >
                              <span className="ref-from-short">
                                {t("s3.refFrom", { flows: r.nutzer.slice(0, 2).join(", ") + (r.nutzer.length > 2 ? ` +${r.nutzer.length - 2}` : "") })}
                              </span>
                              {r.nutzer.length > 2 && (
                                <span className="ref-from-all">
                                  {t("s3.refFrom", { flows: r.nutzer.slice(0, 10).join(", ") })}
                                  {r.nutzer.length > 10 && ` ${t("s3.refFromMore", { n: r.nutzer.length - 10 })}`}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td><Versions source={r.srcVer} target={r.tgtVer} /></td>
                        <td><StatusPill status={r.status} /></td>
                        <td>{state.refSel?.[r.id] && r.gebraucht && r.transportierbar && r.resolvable && !r.draft && <DeploymentSwitch name={r.name}
                          checked={referenceAutoDeploy(state, { id: r.id, referencedBy: r.nutzer })} disabled={!deploymentAvailable}
                          required={referenceDeployRequired(state, { referencedBy: r.nutzer })}
                          onChange={on => dispatch({ type: "SET_REF_DEPLOY", payload: { id: r.id, on } })} />}</td>
                        <td><span className="tiny muted">{t("deploy.edgeIflowsOnly")}</span></td>
                        <td>
                          {/* Genau ein Hinweis je Zeile, in der Reihenfolge der Sperren: nicht
                              transportierbar > nicht aufloesbar > Entwurf > identisch > kein iFlow.
                              Erst wenn die Checkbox bedienbar ist, kommt die Warnung vor dem
                              Ueberschreiben der Zielwerte - sie begleitet die bewusste Freigabe. */}
                          {!r.transportierbar && <span className="tiny" style={{ color: "var(--warn)" }}>{r.grund || t("s3.refNotTransportable")}</span>}
                          {r.transportierbar && !r.resolvable && <span className="tiny" style={{ color: "var(--warn)" }}>{t("s3.refUnres")}</span>}
                          {r.transportierbar && r.resolvable && r.draft && <span className="tiny" style={{ color: "var(--warn)" }}>{t("s3.refDraft")}</span>}
                          {r.transportierbar && r.resolvable && !r.draft && !r.gebraucht && <span className="tiny muted">{t("s3.refNoFlow")}</span>}
                          {r.transportierbar && r.resolvable && !r.draft && r.gebraucht && r.sameVersion && !state.canTransportSameVersionReferences && <span className="tiny muted">{t("s3.sameReferenceVersionBlocked")}</span>}
                          {r.transportierbar && r.resolvable && !r.draft && r.gebraucht && (!r.sameVersion || state.canTransportSameVersionReferences) && r.ueberschreibt && <span className="tiny" style={{ color: "var(--warn)" }}>{t("s3.refOverwrites")}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {/* DEV183 (Entscheidung David): Globale Referenzen aus anderen Paketen werden nicht mitgeliefert, nur gegen das Ziel
              geprüft. Kein Haken, kein Deployment, keine Sperre. */}
          {refChecksShown.length > 0 && (
            <div className="sect panel">
              <div className="panel-h"><h3>{t("refCheck.title")}</h3></div>
              <div className="panel-pad" style={{ paddingTop: 12, paddingBottom: 0 }}>
                <p className="tiny muted" style={{ margin: 0 }}>{t("refCheck.head")}</p>
              </div>
              <div className="panel-pad tablewrap" style={{ paddingTop: 12 }}>
                <table className="dt transport-table ref-check-table" aria-label={t("refCheck.title")}>
                  <thead><tr>
                    <th>{t("th.art")}</th><th>{t("refCheck.package")}</th>
                    <th>{t("deploy.versions", { cx: <Cx /> })}</th><th>{t("th.status")}</th>
                  </tr></thead>
                  <tbody>
                    {refChecksShown.map((r) => <tr key={r.id} data-ref-status={r.status}>
                      <td><span className="art-name">{r.name}</span><div className="art-id">{r.type}</div></td>
                      <td><span className="art-name">{r.packageName || "-"}</span>
                        {r.packageId && r.packageId !== r.packageName && <div className="art-id">{r.packageId}</div>}</td>
                      <td><Versions source={r.srcVer} target={r.tgtVer} /></td>
                      <td>
                        <span style={{ fontWeight: 600, color: REF_STATUS_COLOR[r.status] }}>{t(`refCheck.status.${r.status}`)}</span>
                        {r.hint && <div className="tiny" style={{ color: "var(--iio-text-2)", marginTop: 2 }}>{r.hint}</div>}
                      </td>
                    </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </fieldset>
      )}

      <div className="footbar" style={{ flexWrap: "wrap" }}>
        <button className="btn btn-ghost" onClick={() => dispatch({ type: "SET_STEP", payload: 2 })}>
          <svg className="ic ic-16"><use href="#ic-collapse" /></svg> {t("common.back")}
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <span className="foot-hint">
            {comparing ? t("s3.refreshing") : packageBlocked ? packageTransportText("PACKAGE_TRANSPORT_BLOCKED_NEXT") : wholePackage && leerePakete.length === 0 ? t("pkgT.title") : leerePakete.length > 0 ? t(leerePakete.length > 1 ? "s3.noIflowsBlockMany" : "s3.noIflowsBlockOne", { names: leerePakete.join(", ") }) : selectedRowsCount > 0 ? t("s3.selN", { n: selectedRowsCount }) : t("s3.min")}
          </span>
          <button className="btn btn-cta" onClick={() => dispatch({ type: "SET_STEP", payload: 4 })} disabled={!canNext}>
            {t("s3.next")}
            <span className="btn-arrow"><svg className="ic"><use href="#ic-arrow-right" /></svg></span>
          </button>
        </div>
      </div>

      <BpmnCompareDialog
        open={!!modal}
        onClose={() => setModal(null)}
        sourceInstanceId={modal?.sourceInstanceId}
        targetInstanceId={modal?.targetInstanceId}
        artifactId={modal?.artId}
        targetArtifactId={modal?.targetArtId}
        artifactName={modal?.artName}
        sourcePackageId={state.pkgId}
        sourceVersion={modal?.srcVer}
        targetVersion={modal?.tgtVer}
      />
    </section>
  );
}
